import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { ObjectiveKpiRequired } from './ObjectiveKpiRequired';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';
import {
  buildObjectiveName,
  DEFAULT_OBJECTIVE_VALUE_TYPE,
  normalizeObjectiveValueType,
  parseObjectiveKpiInput,
  valueTypeLabelKey,
  type ObjectiveValueType
} from './objectiveName';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  LinkIcon,
  LinkSlashIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

interface StrategicObjective {
  id: string;
  name: string;
  description?: string;
  kpi_id?: string | null;
  kpi_name?: string | null;
  kpi_value?: number | null;
  value_type?: ObjectiveValueType | null;
  year?: number | null;
  owner_user_id: string;
  owner_name: string;
  tenant_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  strategy_count: number;
  created_at: string;
  updated_at: string;
}

interface ObjectiveFormData {
  name: string;
  description: string;
  kpi_id: string;
  kpi_value: string;
  value_type: ObjectiveValueType;
  year: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
}

interface TenantKpiOption {
  id: string;
  name?: string;
  master_name?: string;
  custom_name?: string;
  unit?: string;
  type?: string;
}

export const ObjectivesManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { listParams, scopeKey } = useAlignmentRoadmapScope();
  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [availableKpis, setAvailableKpis] = useState<TenantKpiOption[]>([]);
  const [availableStrategies, setAvailableStrategies] = useState<any[]>([]);
  const [objectiveStrategyLinks, setObjectiveStrategyLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLinkStrategyDialogOpen, setIsLinkStrategyDialogOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<StrategicObjective | null>(null);
  const [selectedObjectiveForLinking, setSelectedObjectiveForLinking] = useState<StrategicObjective | null>(null);
  const [attachingKpiObjectiveId, setAttachingKpiObjectiveId] = useState<string | null>(null);
  const emptyForm = (): ObjectiveFormData => ({
    name: '',
    description: '',
    kpi_id: '',
    kpi_value: '',
    value_type: DEFAULT_OBJECTIVE_VALUE_TYPE,
    year: '',
    status: 'ACTIVE'
  });

  const valueTypeOptions = [
    { value: 'TARGET_LEVEL', label: t('alignment.objectives.valueTypes.targetLevel') },
    { value: 'TARGET_IMPROVEMENT', label: t('alignment.objectives.valueTypes.targetImprovement') },
    { value: 'TARGET_REDUCTION', label: t('alignment.objectives.valueTypes.targetReduction') }
  ];

  const parseFormKpiValue = (value: string): number | null => parseObjectiveKpiInput(value);

  const objectivePayload = (data: ObjectiveFormData) => {
    const kpi_value = parseFormKpiValue(data.kpi_value);
    const year = data.year.trim() === '' ? null : Number(data.year);
    const kpi = availableKpis.find((row) => row.id === data.kpi_id);
    return {
      name: buildObjectiveName({
        valueType: data.value_type,
        kpiValue: kpi_value,
        kpiUnit: kpi?.unit,
        kpiType: kpi?.type,
        kpiName: kpi ? kpiLabel(kpi) : null,
        year: year !== null && Number.isInteger(year) ? year : null
      }),
      description: data.description.trim() || null,
      kpi_id: data.kpi_id || null,
      kpi_value,
      value_type: data.value_type || DEFAULT_OBJECTIVE_VALUE_TYPE,
      year: year !== null && Number.isInteger(year) ? year : null
    };
  };
  const [formData, setFormData] = useState<ObjectiveFormData>(emptyForm());
  const [linkFormData, setLinkFormData] = useState({
    strategy_id: '',
    relationship_type: 'PRIMARY' as 'PRIMARY' | 'SECONDARY' | 'SUPPORTING',
    contribution_level: 'HIGH' as 'HIGH' | 'MEDIUM' | 'LOW',
    notes: ''
  });

  useEffect(() => {
    if (user?.tenantId) {
      fetchObjectives();
      fetchAvailableKpis();
      fetchAvailableStrategies();
    }
  }, [user?.tenantId, scopeKey]);

  // Fetch objective strategy links when objectives change
  useEffect(() => {
    if (objectives.length > 0) {
      // Clear existing links first
      setObjectiveStrategyLinks([]);
      // Fetch links for each objective sequentially
      const fetchAllObjectiveLinks = async () => {
        for (const objective of objectives) {
          if (objective.strategy_count > 0) {
            await fetchObjectiveStrategyLinks(objective.id);
          }
        }
      };
      fetchAllObjectiveLinks();
    }
  }, [objectives]);

  const fetchObjectives = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/alignment/strategic-objectives/${user?.tenantId}`, listParams);

      if (response.data.success) {
        setObjectives(response.data.data);
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToFetchObjectives'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableKpis = async () => {
    try {
      const response = await api.get('/kpis');
      if (response.data.success) {
        setAvailableKpis(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch KPIs:', err);
    }
  };

  const kpiLabel = (kpi: TenantKpiOption) => kpi.custom_name || kpi.name || kpi.master_name || kpi.id;

  const selectedKpi = availableKpis.find((kpi) => kpi.id === formData.kpi_id);
  const parsedYear = formData.year.trim() === '' ? null : Number(formData.year);
  const generatedName = buildObjectiveName({
    valueType: formData.value_type,
    kpiValue: parseFormKpiValue(formData.kpi_value),
    kpiUnit: selectedKpi?.unit,
    kpiType: selectedKpi?.type,
    kpiName: selectedKpi ? kpiLabel(selectedKpi) : null,
    year: parsedYear !== null && Number.isInteger(parsedYear) ? parsedYear : null
  });

  const fetchAvailableStrategies = async () => {
    try {
      const response = await api.get(`/alignment/strategies/${user?.tenantId}`, listParams);
      if (response.data.success) {
        setAvailableStrategies(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
    }
  };

  const fetchObjectiveStrategyLinks = async (objectiveId: string) => {
    try {
      const response = await api.get(`/alignment/objective-strategies/${objectiveId}`);
      if (response.data.success) {
        // Append new links to existing ones instead of overwriting
        setObjectiveStrategyLinks(prevLinks => {
          // Remove any existing links for this objective
          const filteredLinks = prevLinks.filter(link => link.objective_id !== objectiveId);
          // Add new links for this objective
          return [...filteredLinks, ...response.data.data];
        });
      }
    } catch (err) {
      console.error('Error fetching objective strategy links:', err);
    }
  };

  const handleCreateObjective = async () => {
    try {
      const response = await api.post('/alignment/strategic-objectives', {
        ...objectivePayload(formData),
        owner_user_id: user?.id,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsCreateDialogOpen(false);
        setFormData(emptyForm());
        fetchObjectives();
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToCreateObjective'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleUpdateObjective = async () => {
    if (!editingObjective) return;

    try {
      const response = await api.put(`/alignment/strategic-objectives/${editingObjective.id}`, objectivePayload(formData));

      if (response.data.success) {
        setIsEditDialogOpen(false);
        setEditingObjective(null);
        setFormData(emptyForm());
        fetchObjectives();
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToUpdateObjective'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleDeleteObjective = async (objectiveId: string) => {
    if (!confirm(t('alignment.objectives.deleteConfirm'))) {
      return;
    }

    try {
      const response = await api.delete(`/alignment/strategic-objectives/${objectiveId}`);

      if (response.data.success) {
        fetchObjectives();
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToDeleteObjective'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleLinkStrategy = async () => {
    if (!selectedObjectiveForLinking || !linkFormData.strategy_id) return;

    try {
      const response = await api.post('/alignment/objective-strategy-links', {
        objective_id: selectedObjectiveForLinking.id,
        strategy_id: linkFormData.strategy_id,
        relationship_type: linkFormData.relationship_type,
        contribution_level: linkFormData.contribution_level,
        notes: linkFormData.notes,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsLinkStrategyDialogOpen(false);
        setSelectedObjectiveForLinking(null);
        setLinkFormData({
          strategy_id: '',
          relationship_type: 'PRIMARY',
          contribution_level: 'HIGH',
          notes: ''
        });
        fetchObjectives(); // Refresh to show updated strategy count
        fetchObjectiveStrategyLinks(selectedObjectiveForLinking.id); // Refresh strategy links
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToLinkStrategy'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const handleAttachKpi = async (objective: StrategicObjective, kpiId: string) => {
    try {
      setAttachingKpiObjectiveId(objective.id);
      // Partial update: COALESCE on the server keeps name, vision, and other fields.
      const response = await api.put(`/alignment/strategic-objectives/${objective.id}`, {
        kpi_id: kpiId,
      });

      if (response.data.success) {
        await fetchObjectives();
      } else {
        throw new Error(response.data.message || t('alignment.objectives.errors.failedToAttachKpi'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    } finally {
      setAttachingKpiObjectiveId(null);
    }
  };

  const handleUnlinkStrategy = async (objectiveId: string, strategyId: string) => {
    if (!confirm(t('alignment.objectives.unlinkConfirm'))) return;

    try {
      // Find the objective-strategy link ID
      const link = objectiveStrategyLinks.find(link => 
        link.objective_id === objectiveId && link.strategy_id === strategyId
      );
      
      if (link) {
        await api.delete(`/alignment/objective-strategy-links/${link.id}`);
        fetchObjectives(); // Refresh to show updated strategy count
        fetchObjectiveStrategyLinks(objectiveId); // Refresh strategy links
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.common.errors.anErrorOccurred'));
    }
  };

  const openEditDialog = (objective: StrategicObjective) => {
    setEditingObjective(objective);
    setFormData({
      name: objective.name,
      description: objective.description || '',
      kpi_id: objective.kpi_id || '',
      kpi_value: objective.kpi_value != null ? String(objective.kpi_value) : '',
      value_type: normalizeObjectiveValueType(objective.value_type),
      year: objective.year != null ? String(objective.year) : '',
      status: objective.status
    });
    setIsEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setFormData(emptyForm());
    setIsCreateDialogOpen(true);
  };

  const openLinkStrategyDialog = (objective: StrategicObjective) => {
    setSelectedObjectiveForLinking(objective);
    setLinkFormData({
      strategy_id: '',
      relationship_type: 'PRIMARY',
      contribution_level: 'HIGH',
      notes: ''
    });
    setIsLinkStrategyDialogOpen(true);
  };

  const closeDialogs = () => {
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsLinkStrategyDialogOpen(false);
    setEditingObjective(null);
    setSelectedObjectiveForLinking(null);
    setFormData(emptyForm());
    setLinkFormData({
      strategy_id: '',
      relationship_type: 'PRIMARY',
      contribution_level: 'HIGH',
      notes: ''
    });
  };

  const relationshipTypeOptions = [
    { value: 'PRIMARY', label: t('alignment.objectives.relationshipTypes.primary') },
    { value: 'SECONDARY', label: t('alignment.objectives.relationshipTypes.secondary') },
    { value: 'SUPPORTING', label: t('alignment.objectives.relationshipTypes.supporting') }
  ];

  const contributionLevelOptions = [
    { value: 'HIGH', label: t('alignment.objectives.contributionLevels.high') },
    { value: 'MEDIUM', label: t('alignment.objectives.contributionLevels.medium') },
    { value: 'LOW', label: t('alignment.objectives.contributionLevels.low') }
  ];

  if (loading) {
    return (
      <Card padding="none">
        <CardContent className="p-3">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-end">
        <Button onClick={openCreateDialog} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
          <PlusIcon className="w-4 h-4 mr-2" />
          {t('alignment.objectives.createNew')}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-sm">⚠️</span>
            <span className="text-sm font-medium">{t('alignment.common.error')}</span>
          </div>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Objectives List */}
      <div className="grid gap-2">
        {objectives.map((objective) => (
          <Card key={objective.id} padding="none" className="hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="mb-1">
                    <h3
                      className="text-sm font-semibold text-[var(--color-surface-text)]"
                      title={`${t('alignment.objectives.owner')}: ${objective.owner_name} · ${t('alignment.objectives.created')}: ${new Date(objective.created_at).toLocaleDateString()}`}
                    >
                      {objective.name}
                    </h3>
                    {objective.kpi_name ? (
                      <Badge variant="outline">
                        {t('alignment.objectives.kpi')}: {objective.kpi_name}
                      </Badge>
                    ) : (
                      <ObjectiveKpiRequired
                        options={availableKpis.map((kpi) => ({ id: kpi.id, label: kpiLabel(kpi) }))}
                        disabled={attachingKpiObjectiveId === objective.id}
                        onAttach={(kpiId) => handleAttachKpi(objective, kpiId)}
                      />
                    )}
                    {(objective.kpi_value != null || objective.year) && (
                      <span className="ml-2 text-xs text-[var(--color-surface-text)]">
                        {objective.kpi_value != null ? objective.kpi_value : ''}
                        {objective.value_type
                          ? ` · ${t(valueTypeLabelKey(objective.value_type))}`
                          : ''}
                        {objective.year ? ` · ${objective.year}` : ''}
                      </span>
                    )}
                  </div>
                  {objective.description && (
                    <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                      {objective.description}
                    </p>
                  )}

                  <div className="mt-1">
                    <h4 className="text-xs font-medium text-[var(--color-surface-text)] mb-0.5">{t('alignment.objectives.linkedStrategies')}</h4>
                    {objective.strategy_count > 0 ? (
                      <ul className="list-disc pl-5 text-sm text-[var(--color-surface-text)]">
                        {objectiveStrategyLinks
                          .filter(link => link.objective_id === objective.id)
                          .map((link) => {
                            const strategy = availableStrategies.find(s => s.id === link.strategy_id);
                            return strategy ? (
                              <li key={link.id || `${objective.id}-${strategy.id}`} className="leading-5">
                                <span className="inline-flex items-center gap-1">
                                  {strategy.name}
                                  <button
                                    type="button"
                                    onClick={() => handleUnlinkStrategy(objective.id, strategy.id)}
                                    className="p-0.5 text-red-600 hover:text-red-700 flex-shrink-0"
                                    title={t('alignment.objectives.unlink') || 'Unlink'}
                                    aria-label={t('alignment.objectives.unlink') || 'Unlink'}
                                  >
                                    <LinkSlashIcon className="w-3.5 h-3.5" />
                                  </button>
                                </span>
                              </li>
                            ) : null;
                          })}
                      </ul>
                    ) : (
                      <div className="text-xs text-gray-500 italic">{t('alignment.objectives.noStrategiesLinked')}</div>
                    )}
                  </div>

                </div>

                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openLinkStrategyDialog(objective)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <LinkIcon className="w-4 h-4 mr-1" />
                    {t('alignment.objectives.linkStrategy')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(objective)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteObjective(objective.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {objectives.length === 0 && (
          <Card padding="none">
            <CardContent className="p-4 text-center">
              <div className="text-[var(--color-surface-text-secondary)]">
                <FlagIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">{t('alignment.noObjectivesCreated')}</h3>
                <p className="mb-4">{t('alignment.noObjectivesDescription')}</p>
                <Button onClick={openCreateDialog} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t('alignment.createYourFirstObjective')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Objective Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.objectives.dialogTitles.createNew')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.selectKPI')}</label>
                <AlignmentTypeahead
                  value={formData.kpi_id}
                  onChange={(id) => setFormData({ ...formData, kpi_id: id })}
                  options={availableKpis.map((kpi) => ({ id: kpi.id, label: kpiLabel(kpi) }))}
                  placeholder={t('alignment.objectives.chooseKPI')}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.valueType')}</label>
                  <Select
                    value={formData.value_type}
                    onChange={(e) => setFormData({ ...formData, value_type: e.target.value as ObjectiveValueType })}
                    options={valueTypeOptions}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.kpiValue')}</label>
                  <Input
                    inputMode="decimal"
                    value={formData.kpi_value}
                    onChange={(e) => setFormData({ ...formData, kpi_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.year')}</label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.generatedName')}</label>
                <Input value={generatedName} readOnly />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.objectivesManagement.describeObjective')}
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.objectives.buttonLabels.cancel')}
              </Button>
              <Button 
                onClick={handleCreateObjective} 
                disabled={!generatedName}
                className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]"
              >
                {t('alignment.objectives.buttonLabels.createObjective')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Objective Dialog */}
      {isEditDialogOpen && editingObjective && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.objectives.dialogTitles.edit')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.selectKPI')}</label>
                <AlignmentTypeahead
                  value={formData.kpi_id}
                  onChange={(id) => setFormData({ ...formData, kpi_id: id })}
                  options={availableKpis.map((kpi) => ({ id: kpi.id, label: kpiLabel(kpi) }))}
                  placeholder={t('alignment.objectives.chooseKPI')}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.valueType')}</label>
                  <Select
                    value={formData.value_type}
                    onChange={(e) => setFormData({ ...formData, value_type: e.target.value as ObjectiveValueType })}
                    options={valueTypeOptions}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.kpiValue')}</label>
                  <Input
                    inputMode="decimal"
                    value={formData.kpi_value}
                    onChange={(e) => setFormData({ ...formData, kpi_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('alignment.objectives.year')}</label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.generatedName')}</label>
                <Input value={generatedName} readOnly />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.objectivesManagement.describeObjective')}
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.objectives.buttonLabels.cancel')}
              </Button>
              <Button onClick={handleUpdateObjective} className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                {t('alignment.objectives.buttonLabels.updateObjective')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link Strategy Dialog */}
      {isLinkStrategyDialogOpen && selectedObjectiveForLinking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.objectives.dialogTitles.linkStrategyToObjective')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('alignment.objectives.linkingStrategy')}: <strong>{selectedObjectiveForLinking.name}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.selectStrategy')}</label>
                <AlignmentTypeahead
                  value={linkFormData.strategy_id}
                  onChange={(id) => setLinkFormData({ ...linkFormData, strategy_id: id })}
                  options={availableStrategies.map(s => ({ id: s.id, label: s.name }))}
                  placeholder={t('alignment.objectives.placeholders.chooseStrategy')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.relationshipType')}</label>
                <Select
                  value={linkFormData.relationship_type}
                  onChange={(e) => setLinkFormData({ ...linkFormData, relationship_type: e.target.value as any })}
                  options={relationshipTypeOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.contributionLevel')}</label>
                <Select
                  value={linkFormData.contribution_level}
                  onChange={(e) => setLinkFormData({ ...linkFormData, contribution_level: e.target.value as any })}
                  options={contributionLevelOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('alignment.objectives.formLabels.notesOptional')}</label>
                <Textarea
                  value={linkFormData.notes}
                  onChange={(e) => setLinkFormData({ ...linkFormData, notes: e.target.value })}
                  placeholder={t('alignment.objectives.placeholders.addNotesAboutLink')}
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.objectives.buttonLabels.cancel')}
              </Button>
              <Button 
                onClick={handleLinkStrategy} 
                disabled={!linkFormData.strategy_id}
                className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]"
              >
                {t('alignment.objectives.buttonLabels.linkStrategy')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
